import { useMemo, useState } from "react";
import EmptyState from "@/components/base/EmptyState";
import Modal from "@/components/base/Modal";
import { cn } from "@/utils/cn";
import {
  ACTION_LABELS,
  MODULE_LABELS,
  countModules,
  countPermissions,
  type PermissionAction,
} from "@/store/permissions/catalog";
import { useRoles } from "@/store/permissions/RolesProvider";
import type { RoleEmploye } from "@/store/permissions/rolesData";
import { useEmployes } from "@/pages/agence/employes/EmployesProvider";
import RoleFormModal from "./RoleFormModal";

function describeModules(role: RoleEmploye): string {
  const modules = Object.keys(role.permissions).filter((module) =>
    Object.values(role.permissions[module] ?? {}).some(Boolean)
  );
  if (modules.length === 0) return "Aucune permission";
  return modules
    .slice(0, 4)
    .map((module) => MODULE_LABELS[module] ?? module)
    .join(", ")
    .concat(modules.length > 4 ? ` +${modules.length - 4}` : "");
}

function describeActions(role: RoleEmploye): string {
  const actions = new Set<PermissionAction>();
  Object.values(role.permissions).forEach((moduleActions) => {
    Object.entries(moduleActions ?? {}).forEach(([action, value]) => {
      if (value) actions.add(action as PermissionAction);
    });
  });
  return Array.from(actions)
    .map((action) => ACTION_LABELS[action])
    .join(" · ");
}

export default function RolesPermissionsSection() {
  const { roles, loading, error, reload, deleteRole, canManage } = useRoles();
  const { employes } = useEmployes();
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RoleEmploye | null>(null);
  const [confirm, setConfirm] = useState<RoleEmploye | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const counts = useMemo(() => {
    const map = new Map<string, number>();
    employes.forEach((employe) => {
      const code = employe.role || "";
      map.set(code, (map.get(code) ?? 0) + 1);
    });
    return map;
  }, [employes]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (role: RoleEmploye) => {
    setEditing(role);
    setFormOpen(true);
  };

  const handleDelete = async () => {
    if (!confirm) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await deleteRole(confirm.id);
      setConfirm(null);
    } catch (caught) {
      setDeleteError(caught instanceof Error ? caught.message : "Suppression impossible.");
    } finally {
      setDeleting(false);
    }
  };

  if (!canManage) {
    return (
      <EmptyState
        icon="ri-lock-2-line"
        title="Accès réservé au directeur"
        description="Seul le directeur peut configurer les rôles et permissions de l'organisation."
      />
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-primary-50 text-primary-600">
            <i className="ri-shield-keyhole-line text-lg" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">Rôles et permissions</h3>
            <p className="mt-0.5 text-xs text-foreground-500">
              Créez des rôles propres à votre organisation et cochez leurs permissions par module. Les employés héritent
              des droits de leur rôle, réductibles au cas par cas.
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="inline-flex shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
        >
          <i className="ri-add-line text-base" aria-hidden="true" />
          Nouveau rôle
        </button>
      </section>

      {loading && (
        <div className="flex items-center justify-center gap-2 rounded-lg border border-background-200 bg-background-50 py-10 text-sm text-foreground-500">
          <i className="ri-loader-4-line animate-spin text-lg" aria-hidden="true" />
          Chargement des rôles...
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-primary-200 bg-primary-50/50 px-4 py-8 text-center">
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-100 text-primary-700">
            <i className="ri-error-warning-line text-lg" aria-hidden="true" />
          </span>
          <div>
            <p className="text-sm font-label font-medium text-foreground-950">Impossible de charger les rôles</p>
            <p className="mt-0.5 text-xs text-foreground-600">{error}</p>
          </div>
          <button
            type="button"
            onClick={() => void reload()}
            className="inline-flex items-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-4 py-2 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-refresh-line text-base" aria-hidden="true" />
            Réessayer
          </button>
        </div>
      )}

      {!loading && !error && roles.length === 0 && (
        <EmptyState
          icon="ri-shield-user-line"
          title="Aucun rôle configuré"
          description="Créez votre premier rôle (par exemple Gestionnaire, Comptable ou Caissier) puis définissez ses permissions. Vous pourrez ensuite l'attribuer à vos employés."
          action={
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-add-line text-base" aria-hidden="true" />
              Créer un rôle
            </button>
          }
        />
      )}

      {!loading && !error && roles.length > 0 && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {roles.map((role) => {
            const used = counts.get(role.code) ?? 0;
            const total = countPermissions(role.permissions);
            const modules = countModules(role.permissions);
            return (
              <section
                key={role.id}
                className="flex flex-col gap-3.5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 items-start gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                      <i className="ri-shield-user-line text-lg" aria-hidden="true" />
                    </span>
                    <div className="min-w-0">
                      <h4 className="truncate font-heading text-sm font-semibold text-foreground-950">{role.nom}</h4>
                      <p className="truncate text-xs text-foreground-400">code : {role.code}</p>
                    </div>
                  </div>
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full border border-secondary-200 bg-secondary-100 px-2.5 py-1 text-[11px] font-label font-medium text-secondary-900">
                    <i className="ri-user-line text-xs" aria-hidden="true" />
                    {used} employé{used > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <span className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-2.5 py-1 text-[11px] font-label font-medium text-foreground-700">
                    <i className="ri-checkbox-multiple-line text-xs" aria-hidden="true" />
                    {total} permission{total > 1 ? "s" : ""}
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full border border-background-300 bg-background-100 px-2.5 py-1 text-[11px] font-label font-medium text-foreground-700">
                    <i className="ri-apps-2-line text-xs" aria-hidden="true" />
                    {modules} module{modules > 1 ? "s" : ""}
                  </span>
                </div>

                <div className="min-w-0">
                  <p className="truncate text-xs text-foreground-500">{describeModules(role)}</p>
                  {total > 0 && <p className="mt-1 truncate text-[11px] text-foreground-400">{describeActions(role)}</p>}
                </div>

                <div className="mt-auto flex items-center gap-2 border-t border-background-200 pt-3">
                  <button
                    type="button"
                    onClick={() => openEdit(role)}
                    className="inline-flex flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
                  >
                    <i className="ri-edit-line text-sm" aria-hidden="true" />
                    Modifier
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setDeleteError(null);
                      setConfirm(role);
                    }}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-200 bg-background-50 px-3.5 py-2 text-xs font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
                  >
                    <i className="ri-delete-bin-line text-sm" aria-hidden="true" />
                    Supprimer
                  </button>
                </div>
              </section>
            );
          })}
        </div>
      )}

      <RoleFormModal open={formOpen} onClose={() => setFormOpen(false)} role={editing} />

      <Modal
        open={Boolean(confirm)}
        onClose={() => setConfirm(null)}
        size="sm"
        title="Supprimer le rôle"
        description={
          confirm
            ? `Voulez-vous vraiment supprimer le rôle « ${confirm.nom} » ? Cette action est définitive.`
            : undefined
        }
      >
        <div className="flex flex-col gap-4">
          {deleteError && (
            <p className="flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50/60 px-3.5 py-2.5 text-xs text-primary-800">
              <i className="ri-error-warning-line mt-0.5 text-sm" aria-hidden="true" />
              {deleteError}
            </p>
          )}
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              type="button"
              onClick={() => setConfirm(null)}
              className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={() => void handleDelete()}
              disabled={deleting}
              className={cn(
                "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer",
                deleting && "cursor-not-allowed opacity-60"
              )}
            >
              <i className={cn(deleting ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line", "text-base")} aria-hidden="true" />
              Supprimer
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}